// @flow
import React, {useState, useEffect} from 'react';
import {
  makeStyles,
  Grid,
  InputAdornment,
  Snackbar,
  CircularProgress,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import {useForm} from 'react-hook-form';
import {useHistory, useParams} from 'react-router-dom';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import CustomSelect from '../../../../elements/CustomSelect/CustomSelect';
import CustomExitPrompt from '../../../../elements/CustomExitPrompt/CustomExitPrompt';
import {
  useCreateLogisticPickupRate,
  useGetCurrencyCountry,
  useGetLogisticPickupRate,
  useUpdateLogisticPickupRate,
  useResetState,
} from './utils';
import {getConfigurationAdmin} from '../../../../utils/location';
import {useStoreCountry} from '../../../country/utils';
import {useAdminAddress} from '../../../address/utils/useAdminAddress';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '12px',
    backgroundColor: 'white',
    // border: `1px solid #e5e6e7`,
  },
  contentBackground: {
    backgroundColor: '#fff',
    borderRadius: '12px',
  },
  messageSpace: {
    marginBottom: theme.spacing(2, 0),
    alignSelf: 'stretch',
    display: 'flex',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  messageField: {
    flexGrow: 1,
    marginBottom: theme.spacing(1),
  },
  saveButton: {
    marginTop: theme.spacing(2),
    width: 'fit-content',
    paddingInline: theme.spacing(8),
  },
  select: {
    backgroundColor: 'white',
  },
  form: {
    backgroundColor: 'white',
    padding: theme.spacing(2),
    borderBottomLeftRadius: '12px',
    borderBottomRightRadius: '12px',
    border: '1px solid #e5e6e7',
    borderTop: 'none',
  },
}));

export default function LogisticPickupNewNational() {
  const classes = useStyles();
  const history = useHistory();
  const {adminRateId} = useParams();

  const currency = useGetCurrencyCountry();
  const country = useStoreCountry();
  const [alertOpenError, setAlertOpenError] = useState(false);
  const [alertOpenErroLocation, setAlertOpenErroLocation] = useState(false);
  const {register, errors, handleSubmit, formState} = useForm();
  const {isDirty} = formState;
  const {resetState} = useResetState();

  const {
    createPickupRate,
    isSaving,
    savingSuccess,
    savingError,
  } = useCreateLogisticPickupRate();
  const {fetchRate, pickupRate} = useGetLogisticPickupRate();
  const {updatePickupRate} = useUpdateLogisticPickupRate();

  const adminConfigCountry = getConfigurationAdmin(country?.code);
  const {
    admin1List,
    admin2List,
    admin3List,
    selectAdmin1,
    selectAdmin2,
    selectAdmin3,
    selectedAdmin1,
    selectedAdmin2,
    selectedAdmin3,
    setAdmins,
  } = useAdminAddress();

  const handleFormSubmit = data => {
    const {price, displayName, address1, ...rest} = data;

    if (selectedAdmin1 && !selectedAdmin2) {
      setAlertOpenErroLocation(true);
      if (adminConfigCountry?.admin3?.enabled && !selectedAdmin3) {
        setAlertOpenErroLocation(true);
        return;
      }
    }

    const pickupMethodRequest = {
      address: {
        ...rest,
        address1: address1 ? address1.trim() : undefined,
        displayName,
      },
      enabled: pickupRate ? pickupRate.enabled : true,
      price,
    };

    if (selectedAdmin1 && selectAdmin2) {
      pickupMethodRequest.address = {
        ...pickupMethodRequest.address,
        admin1: selectedAdmin1?.adminId,
        admin2: selectedAdmin2?.adminId,
        admin3: selectedAdmin3?.adminId,
        country: country.code,
      };
    }

    console.log({pickupMethodRequest});

    if (adminRateId && pickupRate) {
      // $FlowFixMe
      pickupMethodRequest.adminRateId = adminRateId;
      updatePickupRate(pickupMethodRequest);
      return;
    }

    createPickupRate(pickupMethodRequest);
  };

  useEffect(() => {
    if (adminRateId && pickupRate && pickupRate.admin1) {
      setAdmins(
        pickupRate.admin1.admin1Id.toString(),
        pickupRate?.admin2?.admin2Id.toString(),
        pickupRate?.admin3?.admin3Id.toString()
      );
    }
  }, [adminRateId, pickupRate, setAdmins]);

  useEffect(() => {
    if (adminRateId) {
      fetchRate(adminRateId);
    }
  }, [adminRateId, fetchRate]);

  useEffect(() => {
    if (savingSuccess) {
      resetState();
      history.push('/configure/logistics/pickup');
    }
  }, [history, resetState, savingSuccess]);

  useEffect(() => {
    if (savingError) {
      setAlertOpenError(true);
    }
  }, [savingError]);

  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  return (
    <div className={classes.root}>
      <CustomExitPrompt when={isDirty && !savingSuccess} />
      <Snackbar
        open={alertOpenError}
        autoHideDuration={4000}
        onClose={() => setAlertOpenError(false)}
      >
        <Alert severity="error" onClose={() => setAlertOpenError(false)}>
          Ocurrió un error al guardar la configuración
        </Alert>
      </Snackbar>
      <Snackbar
        open={alertOpenErroLocation}
        autoHideDuration={4000}
        onClose={() => setAlertOpenErroLocation(false)}
      >
        <Alert severity="error" onClose={() => setAlertOpenErroLocation(false)}>
          Porfavor complete la ubicación
        </Alert>
      </Snackbar>
      {!adminRateId && (
        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className={classes.form}
        >
          <div className={classes.contentBackground}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <CustomTextField
                  className={classes.textField}
                  label="Dirección"
                  InputProps={{
                    className: classes.textInput,
                  }}
                  InputLabelProps={{className: classes.textInputLabel}}
                  name="address1"
                  error={!!errors.address1}
                  helperText={errors.address1 ? errors.address1.message : ''}
                  inputRef={register()}
                  type="text"
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <CustomTextField
                  className={classes.textField}
                  label="Nombre"
                  InputProps={{
                    className: classes.textInput,
                  }}
                  InputLabelProps={{className: classes.textInputLabel}}
                  name="displayName"
                  error={!!errors.displayName}
                  helperText={
                    errors.displayName ? errors.displayName.message : ''
                  }
                  inputRef={register({
                    required: 'Campo obligatorio',
                  })}
                  type="text"
                  disabled={isSaving}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <CustomSelect
                  native
                  fullWidth
                  className={classes.select}
                  label={adminConfigCountry?.admin1?.label || 'Departamento'}
                  error={!!errors.admin1}
                  helperText={errors.admin1 ? errors.admin1.message : ''}
                  disabled={isSaving}
                  onChange={e => {
                    const adminId = e.target.value;
                    const adminAddress = admin1List.find(
                      admin => admin.adminId.toString() === adminId
                    );

                    if (!adminAddress) return;

                    selectAdmin1(adminAddress);
                  }}
                >
                  <option value={'none'} hidden />
                  {admin1List.map((c, index) => (
                    <option key={`admin1-${c.adminId}`} value={c.adminId}>
                      {c.adminName}
                    </option>
                  ))}
                </CustomSelect>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <CustomSelect
                  native
                  fullWidth
                  className={classes.select}
                  label={adminConfigCountry?.admin2?.label || 'Provincia'}
                  error={!!errors.admin2}
                  helperText={errors.admin2 ? errors.admin2.message : ''}
                  disabled={isSaving || admin2List.length === 0}
                  value={selectedAdmin2?.adminId || 'none'}
                  onChange={e => {
                    const adminId = e.target.value;
                    const adminAddress = admin2List.find(
                      admin => admin.adminId.toString() === adminId
                    );

                    if (!adminAddress) return;

                    selectAdmin2(adminAddress);
                  }}
                >
                  <option value={'none'} hidden />
                  {admin2List.map((c, index) => (
                    <option key={`admin2-${c.adminId}`} value={c.adminId}>
                      {c.adminName}
                    </option>
                  ))}
                </CustomSelect>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              {adminConfigCountry?.admin3?.enabled && (
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <CustomSelect
                    native
                    fullWidth
                    className={classes.select}
                    label={adminConfigCountry?.admin3?.label || 'Distrito'}
                    error={!!errors.admin3}
                    helperText={errors.admin3 ? errors.admin3.message : ''}
                    disabled={isSaving || admin3List.length === 0}
                    value={selectedAdmin3?.adminId || 'none'}
                    onChange={e => {
                      const adminId = e.target.value;
                      const adminAddress = admin3List.find(
                        admin => admin.adminId.toString() === adminId
                      );

                      if (!adminAddress) return;

                      selectAdmin3(adminAddress);
                    }}
                  >
                    <option value={'none'} hidden />
                    {admin3List.map((c, index) => (
                      <option key={`admin3-${c.adminId}`} value={c.adminId}>
                        {c.adminName}
                      </option>
                    ))}
                  </CustomSelect>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <CustomTextField
                  className={classes.textField}
                  label="Precio"
                  InputProps={{
                    className: classes.textInput,
                    startAdornment: (
                      <InputAdornment position="start">
                        {currency ? currency.symbol : ''}
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{className: classes.textInputLabel}}
                  name="price"
                  error={!!errors.price}
                  helperText={errors.price ? errors.price.message : ''}
                  inputRef={register({
                    required: 'Campo obligatorio',
                  })}
                  type="number"
                  inputProps={{step: '.01'}}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <div className={classes.messageSpace}>
                  <CustomTextField
                    multiline
                    rows={5}
                    rowsMax={18}
                    label="Instrucciones de recojo"
                    className={`${classes.textField} ${classes.messageField}`}
                    InputProps={{
                      className: classes.textInput,
                    }}
                    InputLabelProps={{className: classes.textInputLabel}}
                    name="description"
                    error={!!errors.description}
                    helperText={errors.description ? errors.description : ''}
                    type="text"
                    inputRef={register}
                    disabled={isSaving}
                  />
                </div>
              </Grid>
            </Grid>
          </div>
          <CustomButton
            variant="contained"
            color="primary"
            type="submit"
            className={classes.saveButton}
            disabled={isSaving}
          >
            {isSaving ? (
              <CircularProgress className={classes.loadingSpinner} size={24} />
            ) : (
              'Guardar'
            )}
          </CustomButton>
        </form>
      )}

      {pickupRate && adminRateId && (
        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className={classes.form}
        >
          <div className={classes.contentBackground}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <CustomTextField
                  className={classes.textField}
                  defaultValue={pickupRate.address1 || ''}
                  label="Dirección"
                  InputProps={{
                    className: classes.textInput,
                  }}
                  InputLabelProps={{className: classes.textInputLabel}}
                  name="address1"
                  error={!!errors.address1}
                  helperText={errors.address1 ? errors.address1.message : ''}
                  inputRef={register({
                    required: 'Campo obligatorio',
                  })}
                  type="text"
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <CustomTextField
                  className={classes.textField}
                  defaultValue={pickupRate.displayName || ''}
                  label="Nombre"
                  InputProps={{
                    className: classes.textInput,
                  }}
                  InputLabelProps={{className: classes.textInputLabel}}
                  name="displayName"
                  error={!!errors.displayName}
                  helperText={
                    errors.displayName ? errors.displayName.message : ''
                  }
                  inputRef={register({
                    required: 'Campo obligatorio',
                  })}
                  type="text"
                  disabled={isSaving}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <CustomSelect
                  native
                  fullWidth
                  className={classes.select}
                  label={adminConfigCountry?.admin1?.label || 'Departamento'}
                  error={!!errors.admin1}
                  helperText={errors.admin1 ? errors.admin1.message : ''}
                  disabled={isSaving}
                  value={selectedAdmin1?.adminId}
                  onChange={e => {
                    const adminId = e.target.value;
                    const adminAddress = admin1List.find(
                      admin => admin.adminId.toString() === adminId
                    );

                    if (!adminAddress) return;

                    selectAdmin1(adminAddress);
                  }}
                >
                  <option value={'none'} hidden />
                  {admin1List.map((c, index) => (
                    <option key={`admin1-${c.adminId}`} value={c.adminId}>
                      {c.adminName}
                    </option>
                  ))}
                </CustomSelect>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <CustomSelect
                  native
                  fullWidth
                  className={classes.select}
                  label={adminConfigCountry?.admin2?.label || 'Provincia'}
                  error={!!errors.admin2}
                  helperText={errors.admin2 ? errors.admin2.message : ''}
                  disabled={isSaving || admin2List.length === 0}
                  value={selectedAdmin2?.adminId || 'none'}
                  onChange={e => {
                    const adminId = e.target.value;
                    const adminAddress = admin2List.find(
                      admin => admin.adminId.toString() === adminId
                    );

                    if (!adminAddress) return;

                    selectAdmin2(adminAddress);
                  }}
                >
                  <option value={'none'} hidden />
                  {admin2List.map((c, index) => (
                    <option key={`admin2-${c.adminId}`} value={c.adminId}>
                      {c.adminName}
                    </option>
                  ))}
                </CustomSelect>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              {adminConfigCountry?.admin3?.enabled && (
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <CustomSelect
                    native
                    fullWidth
                    className={classes.select}
                    label={adminConfigCountry?.admin3?.label || 'Distrito'}
                    error={!!errors.admin3}
                    helperText={errors.admin3 ? errors.admin3.message : ''}
                    disabled={isSaving || admin3List.length === 0}
                    value={selectedAdmin3?.adminId || 'none'}
                    onChange={e => {
                      const adminId = e.target.value;
                      const adminAddress = admin3List.find(
                        admin => admin.adminId.toString() === adminId
                      );

                      if (!adminAddress) return;

                      selectAdmin3(adminAddress);
                    }}
                  >
                    <option value={'none'} hidden />
                    {admin3List.map((c, index) => (
                      <option key={`admin3-${c.adminId}`} value={c.adminId}>
                        {c.adminName}
                      </option>
                    ))}
                  </CustomSelect>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <CustomTextField
                  className={classes.textField}
                  defaultValue={pickupRate.shippingRates[0].price || ''}
                  label="Precio"
                  InputProps={{
                    className: classes.textInput,
                    startAdornment: (
                      <InputAdornment position="start">
                        {currency ? currency.symbol : ''}
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{className: classes.textInputLabel}}
                  name="price"
                  error={!!errors.price}
                  helperText={errors.price ? errors.price.message : ''}
                  inputRef={register({
                    required: 'Campo obligatorio',
                  })}
                  type="number"
                  inputProps={{step: '.01'}}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <div className={classes.messageSpace}>
                  <CustomTextField
                    multiline
                    defaultValue={pickupRate.description || ''}
                    rows={5}
                    rowsMax={18}
                    label="Instrucciones de recojo"
                    className={`${classes.textField} ${classes.messageField}`}
                    InputProps={{
                      className: classes.textInput,
                    }}
                    InputLabelProps={{className: classes.textInputLabel}}
                    name="description"
                    error={!!errors.description}
                    helperText={errors.description ? errors.description : ''}
                    type="text"
                    inputRef={register}
                    disabled={isSaving}
                  />
                </div>
              </Grid>
            </Grid>
          </div>
          <CustomButton
            variant="contained"
            color="primary"
            type="submit"
            className={classes.saveButton}
            disabled={isSaving}
          >
            {isSaving ? (
              <CircularProgress className={classes.loadingSpinner} size={24} />
            ) : (
              'Guardar'
            )}
          </CustomButton>
        </form>
      )}
    </div>
  );
}
